import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Wrench, Truck, Ruler, ChevronDown } from 'lucide-react';
import { findProduct } from '../../content/products';
import { hub } from '../../content/shopContent';
import { chooserMedia } from '../../content/chooserMedia';
import { ProductCard } from '../../components/shop/ShopBits';
import TrustStrip from '../../components/TrustStrip';
import usePageMeta from '../../hooks/usePageMeta';

// The shop's front page exists to turn a visitor into a lock sale and install (Hugh, 17 Sep
// 2026). The locks for sale come straight under the hero; the walk-through that does the
// selling lives on its own page (/shop/find-my-lock) and is pushed from the right-hand
// column, with the lanes list under it.
const promiseIcons = [Wrench, Ruler, Truck, ShieldCheck];
const GUIDES = [
  { to: '/shop/how-to-measure', label: 'How to measure the backset' },
  { to: '/shop/will-it-fit', label: 'Will it fit?' },
  { to: '/shop/installation', label: 'Supply & install, the prices' },
  { to: '/shop/trade', label: 'Trade & property managers' },
];

// The tool as a sign on the right: two small door photos, the name, one line, a button
// (Hugh, 17 Sep: a better sign, a tad smaller). On a wide screen it slides right so it sits
// centred in the white space beside the locks — the right column plus the page margin.
const ToolSign = () => (
  <Link to="/shop/find-my-lock" className="group block rounded-2xl bg-red-600 text-white overflow-hidden shadow-md ring-4 ring-red-100 hover:shadow-xl hover:-translate-y-0.5 transition-all lg:sticky lg:top-24 min-[1280px]:translate-x-[calc(1rem_+_(100vw_-_80rem)_/_4)]" data-testid="hub-find-my-lock-cta">
    <div className="grid grid-cols-2 gap-1 p-2 pb-0" aria-hidden="true">
      <img src={chooserMedia.slides.slides.sm} alt="" loading="lazy" className="w-full aspect-[4/3] object-cover rounded-xl" style={{ objectPosition: chooserMedia.slides.slides.pos }} />
      <img src={chooserMedia.slides.swings.sm} alt="" loading="lazy" className="w-full aspect-[4/3] object-cover rounded-xl" />
    </div>
    <div className="px-4 pt-3 pb-4 text-center">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-red-100">Which lock fits my door?</p>
      <p className="text-2xl font-bold leading-tight mt-0.5">Find my lock</p>
      <p className="text-xs text-red-50 mt-1.5">Three quick taps. The locks that fit, with the fitted price.</p>
      <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white text-red-700 font-bold text-sm px-4 py-2 group-hover:bg-red-50">Start <ArrowRight size={16} /></span>
    </div>
  </Link>
);

// The lanes as a plain list of names, flush with the heading — no icons (Hugh, 17 Sep).
// Always open on a wide screen; a drop-down on a phone.
const LaneNav = () => {
  const [open, setOpen] = useState(false);
  return (
    <nav aria-label="Shop by door" data-testid="hub-lanes">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="lg:hidden w-full flex items-center justify-between rounded-xl border border-gray-300 bg-white px-4 py-3 font-semibold text-gray-900" data-testid="hub-lanes-toggle">
        Shop by door <ChevronDown size={18} className={`text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`${open ? 'block' : 'hidden'} lg:block mt-2 lg:mt-0 rounded-xl border border-gray-200 bg-white lg:border-0 lg:bg-transparent`}>
        <p className="hidden lg:block text-sm font-semibold uppercase tracking-widest text-gray-500 mb-2">Shop by door</p>
        <ul className="divide-y divide-gray-100 lg:divide-y-0">
          {hub.lanes.map((lane) => (
            <li key={lane.slug}>
              <Link to={`/shop/${lane.slug}`} className="block px-3 py-2.5 lg:px-0 lg:py-1.5 font-semibold text-gray-900 leading-snug hover:text-red-700" data-testid={`lane-${lane.slug}`}>
                {lane.title}
              </Link>
            </li>
          ))}
        </ul>
        <p className="text-sm font-semibold uppercase tracking-widest text-gray-500 mt-5 mb-1 px-3 lg:px-0">Guides</p>
        <ul>
          {GUIDES.map((g) => (
            <li key={g.to}>
              <Link to={g.to} className="block px-3 py-1.5 lg:px-0 text-sm text-gray-700 hover:text-red-700">{g.label}</Link>
            </li>
          ))}
          <li>
            <Link to="/shop/find-my-lock" className="block px-3 py-1.5 lg:px-0 text-sm text-gray-700 hover:text-red-700">Digital lock or smart lock?</Link>
          </li>
        </ul>
      </div>
    </nav>
  );
};

const ShopHub = () => {
  usePageMeta();
  const showcase = hub.showcase.map(findProduct).filter(Boolean);
  return (
    <div className="min-h-screen bg-white">
      <section className="relative bg-gray-950 py-10 sm:py-12 text-white overflow-hidden">
        <div className="absolute inset-y-0 right-0 w-full md:w-1/2" aria-hidden="true">
          {/* Lineup composed from the manufacturers' packshots: scripts/build-shop-hero.py */}
          <img
            src="/images/shop/hero-locks.jpg"
            srcSet="/images/shop/hero-locks-sm.jpg 1000w, /images/shop/hero-locks.jpg 2000w"
            sizes="(min-width: 768px) 50vw, 100vw"
            width={2000}
            height={900}
            alt=""
            className="w-full h-full object-cover"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-gray-950/80 md:bg-transparent md:bg-gradient-to-r md:from-gray-950 md:via-gray-950/40 md:to-transparent"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h1 className="text-3xl sm:text-4xl font-bold mb-3">{hub.h1}</h1>
            <p className="text-lg text-gray-200">{hub.positioning}</p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        {/* Three columns: the lanes list left, the locks in the middle, the tool sign right. */}
        <div className="grid lg:grid-cols-[210px_minmax(0,1fr)_250px] gap-8 lg:gap-10">
          <aside className="order-3 lg:order-1">
            <LaneNav />
          </aside>
          <div className="min-w-0 order-2">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Our range</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="hub-showcase">
              {showcase.map((p) => (
                <ProductCard key={p.slug} product={p} />
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2">
              <Link to="/shop/digital-door-locks" className="text-blue-600 font-semibold inline-flex items-center gap-1">
                See every lock <ArrowRight size={16} />
              </Link>
              <Link to="/shop/installation" className="text-blue-600 font-semibold inline-flex items-center gap-1">
                How supply &amp; install works <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mt-10 pt-8 border-t border-gray-200">
              {hub.promises.map((p, i) => {
                const Icon = promiseIcons[i % promiseIcons.length];
                return (
                  <div key={p.title} className="flex gap-3">
                    <Icon className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <h3 className="font-bold text-gray-900">{p.title}</h3>
                      <p className="text-sm text-gray-700">{p.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <aside className="order-1 lg:order-3" data-testid="hub-find-my-lock">
            <ToolSign />
          </aside>
        </div>
      </div>
      <TrustStrip />
    </div>
  );
};

export default ShopHub;
